import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Loader2, CheckCircle2, User, Phone, Mail } from "lucide-react";
import { addEnquiry } from "@/lib/portal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LeadForm({ country, services, onClose }) {
  const [form, setForm] = useState({ firstName: "", mobile: "", email: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const err = {};
    if (!form.firstName.trim()) err.firstName = "First name is required";
    if (!form.mobile.trim() || form.mobile.replace(/\D/g, "").length < 6) err.mobile = "Enter a valid mobile number";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) err.email = "Enter a valid email";
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        country_code: country.code,
        country_name: country.name,
        objective: country.objective,
        name: form.firstName.trim(),
        mobile: form.mobile.trim(),
        email: form.email.trim(),
        services: services || [],
      });
      addEnquiry(country.code);
      setDone(true);
    } catch {
      setErrors({ submit: "Something went wrong — please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {country && (
        <>
          <motion.div
            className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            data-testid="lead-form-modal"
            className="fixed inset-x-0 bottom-0 z-[71] mx-auto max-w-lg rounded-t-3xl border-t border-white/10"
            style={{ background: "rgba(10,14,23,0.99)", boxShadow: "0 -20px 60px rgba(0,0,0,0.6)" }}
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
          >
            <div className="flex justify-center pt-3">
              <div className="h-1.5 w-12 rounded-full bg-white/20" />
            </div>

            <div className="px-5 pb-7 pt-3">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-amber-400/90">Request Information</p>
                  <h2 className="mt-1 flex items-center gap-2 text-xl font-semibold text-white">
                    <span className="text-2xl">{country.flag}</span>
                    {country.name}
                  </h2>
                </div>
                <button
                  data-testid="lead-form-close"
                  onClick={onClose}
                  className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-white/10 text-slate-300"
                >
                  <X size={16} />
                </button>
              </div>

              {/* country requested chip */}
              <div
                data-testid="lead-form-country"
                className="mt-3 flex items-center justify-between rounded-xl border px-3 py-2.5"
                style={{ borderColor: "rgba(226,194,117,0.3)", background: "rgba(226,194,117,0.08)" }}
              >
                <span className="text-[11px] uppercase tracking-wider text-slate-300">Country requested</span>
                <span className="text-sm font-semibold text-amber-200">{country.flag} {country.name}</span>
              </div>

              {services && services.length > 0 && !done && (
                <div data-testid="lead-form-services" className="mt-3 rounded-xl border border-white/[0.06] bg-white/[0.03] p-3">
                  <p className="text-[10px] uppercase tracking-wider text-slate-400">Services you selected ({services.length})</p>
                  <ul className="mt-1.5 space-y-1">
                    {services.map((s, i) => (
                      <li key={i} className="flex items-start gap-1.5 text-[12px] text-slate-300">
                        <span className="mt-1 h-1 w-1 shrink-0 rounded-full bg-emerald-400" /> {s}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {done ? (
                <div data-testid="lead-form-success" className="mt-5 flex flex-col items-center py-6 text-center">
                  <div className="grid h-16 w-16 place-items-center rounded-2xl bg-emerald-400/15 text-emerald-300">
                    <CheckCircle2 size={30} />
                  </div>
                  <h3 className="mt-4 text-lg font-semibold text-white">Thank you, {form.firstName.split(" ")[0]}!</h3>
                  <p className="mt-1 max-w-xs text-sm text-slate-300">
                    Your adviser will be in touch shortly with more information on {country.name}.
                  </p>
                  <button
                    data-testid="lead-form-done"
                    onClick={onClose}
                    className="mt-5 rounded-full bg-amber-300 px-6 py-2.5 text-sm font-semibold text-[#07090E]"
                  >
                    Done
                  </button>
                </div>
              ) : (
                <>
                  <p className="mt-4 text-xs text-slate-300">
                    Share your details and we'll send tailored information. All fields required.
                  </p>

                  <div className="mt-3 space-y-3">
                    <Field
                      testid="lead-first-name"
                      icon={User}
                      placeholder="First name"
                      value={form.firstName}
                      onChange={set("firstName")}
                      error={errors.firstName}
                    />
                    <Field
                      testid="lead-mobile"
                      icon={Phone}
                      type="tel"
                      placeholder="Mobile number"
                      value={form.mobile}
                      onChange={set("mobile")}
                      error={errors.mobile}
                    />
                    <Field
                      testid="lead-email"
                      icon={Mail}
                      type="email"
                      placeholder="Email address"
                      value={form.email}
                      onChange={set("email")}
                      error={errors.email}
                    />
                  </div>

                  {errors.submit && <p className="mt-2 text-xs text-red-400">{errors.submit}</p>}

                  <button
                    data-testid="lead-form-submit"
                    onClick={submit}
                    disabled={submitting}
                    className="mt-4 flex w-full items-center justify-center gap-2 rounded-2xl bg-amber-300 py-3.5 text-sm font-semibold text-[#07090E] transition-transform active:scale-[0.98] disabled:opacity-60"
                  >
                    {submitting ? <Loader2 size={17} className="animate-spin" /> : <Send size={16} />}
                    {submitting ? "Sending…" : "Request Information"}
                  </button>
                  <p className="mt-2 text-center text-[10px] text-slate-400">
                    By submitting you agree to be contacted about your enquiry.
                  </p>
                </>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function Field({ testid, icon: Icon, placeholder, value, onChange, error, type = "text" }) {
  return (
    <div>
      <div
        className="flex items-center gap-2.5 rounded-xl border bg-white/[0.03] px-3.5 py-3"
        style={{ borderColor: error ? "rgba(248,113,113,0.5)" : "rgba(255,255,255,0.1)" }}
      >
        <Icon size={16} className="shrink-0 text-slate-400" />
        <input
          data-testid={testid}
          type={type}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          className="w-full bg-transparent text-sm text-white outline-none placeholder:text-slate-400"
        />
      </div>
      {error && <p className="mt-1 pl-1 text-[11px] text-red-400">{error}</p>}
    </div>
  );
}
